import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';

// form
import { useForm } from 'react-hook-form';
import { LoadingButton } from '@mui/lab';

// @mui
import { FormHelperText, Stack, Typography, TextField, IconButton, InputAdornment, Alert, Card, Box, Divider } from '@mui/material';
import Iconify from '../../components/iconify';
// components

import FormProvider from '../../components/hook-form';
import LoadingProgressBar from '../../components/loading-progressbar/loadingprogressbar';
import { PATH_MASTERS } from '../../routes/paths';
import UserLoginService from '../../services/userlogin.service';
import DataImportService from '../../services/dataimport.service';
import AppCache from '../../services/appcache';


// ----------------------------------------------------------------------

const POLL_INTERVAL_MS = 5000; // 5 seconds

export default function AuthLoginForm() {
    const navigate = useNavigate();
    const { email: paramEmail, id } = useParams(); // Dynamic params from URL
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(false);
    const [validationMessage, setValidationMessage] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isDataImporting, setIsDataImporting] = useState(false);
    const pollTimerRef = useRef(null);

    const [uptimeData, setUptimeData] = useState(null);
    const [liveServerTime, setLiveServerTime] = useState(null);
    const [targetCompletionTime, setTargetCompletionTime] = useState(null);

    const checkDataImportStatus = async () => {
        try {
            const service = await DataImportService;
            const response = await service.GetIsRunningDataImporting();
            const importing = response?.data?.isRunningDataImporting === true ||
                response?.data?.IsRunningDataImporting === true;
            setIsDataImporting(importing);
            if (importing && response?.data) {
                const currentServerTimeVal = response.data.currentServerTime || response.data.CurrentServerTime;
                const totalImportingTimeVal = Number(response.data.totalImportingTime || response.data.TotalImportingTime || 0);
                const estimatedCompletionTimeVal = response.data.estimatedCompletionTime || response.data.EstimatedCompletionTime || null;
                const latestStartTimeVal = response.data.latestStartTime || response.data.LatestStartTime || null;

                const data = {
                    currentServerTime: currentServerTimeVal,
                    totalImportingTime: totalImportingTimeVal,
                    estimatedCompletionTime: estimatedCompletionTimeVal,
                    latestStartTime: latestStartTimeVal
                };
                setUptimeData(data);

                const serverTimeDate = currentServerTimeVal ? new Date(currentServerTimeVal) : new Date();
                setLiveServerTime(serverTimeDate);

                let totalMins = totalImportingTimeVal;
                if (totalMins === 0) {
                    if (estimatedCompletionTimeVal && latestStartTimeVal) {
                        totalMins = Math.max(1, Math.round((new Date(estimatedCompletionTimeVal).getTime() - new Date(latestStartTimeVal).getTime()) / 60000));
                    } else {
                        totalMins = 3;
                    }
                }

                if (estimatedCompletionTimeVal) {
                    setTargetCompletionTime(new Date(estimatedCompletionTimeVal));
                } else if (latestStartTimeVal) {
                    setTargetCompletionTime(new Date(new Date(latestStartTimeVal).getTime() + totalMins * 60000));
                } else {
                    setTargetCompletionTime((prevTarget) => {
                        if (prevTarget) {
                            return prevTarget;
                        }
                        return new Date(serverTimeDate.getTime() + totalMins * 60000);
                    });
                }
            } else {
                setUptimeData(null);
                setTargetCompletionTime(null);
            }
        } catch (error) {
            console.error('Error checking data import status:', error);
            setIsDataImporting(false);
            setUptimeData(null);
            setTargetCompletionTime(null);
        }
    };

    useEffect(() => {
        if (!liveServerTime) {
            return () => { };
        }

        const timer = setInterval(() => {
            setLiveServerTime((prevTime) => new Date(prevTime.getTime() + 1000));
        }, 1000);

        return () => clearInterval(timer);
    }, [liveServerTime]);

    const formatTimeOnly = (dateObj) => {
        if (!dateObj || Number.isNaN(dateObj.getTime())) return 'N/A';

        let hours = dateObj.getHours();
        const minutes = String(dateObj.getMinutes()).padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours %= 12;
        hours = hours || 12;

        return `${hours}:${minutes} ${ampm}`;
    };

    const defaultValues = {
        email: paramEmail || '', // Use dynamic email if present, otherwise empty
        password: '',
    };

    const methods = useForm({

        defaultValues,
    });

    const { handleSubmit, register, watch } = methods;
    const values = watch();

    // ─── Poll IsRunningDataImporting on mount ──────────────────────────────
    useEffect(() => {
        // Check immediately on mount
        checkDataImportStatus();

        // Poll every 5 seconds
        pollTimerRef.current = setInterval(checkDataImportStatus, POLL_INTERVAL_MS);

        return () => {
            if (pollTimerRef.current) {
                clearInterval(pollTimerRef.current);
            }
        };
    }, []);


    const onSubmit = async (data) => {
        setErrorMessage(true)
        if (!data.email || !data.password) {
            return
        }
        setLoading(true);
        try { // NOSONAR
            const payload = { LoginEmailId: data.email, Password: data.password };
            UserLoginService.then((userServiceInstance) => {
                userServiceInstance.UserLogin(payload)
                    .then((response) => {
                        if (response.status === 200) {
                            AppCache.SetCurrentLoggedInUser(response.data);
                            AppCache.GetCurrentLoggedInUser();
                            const isClaimUrl = window.location.href.includes('ContractClaimApproval'); // Check if 'claim' is in the URL
                            const isGmailAndId = paramEmail && id;

                            if (isClaimUrl && isGmailAndId) {
                                // ClaimApproval Claim Login
                                sessionStorage.setItem('email', paramEmail);
                                sessionStorage.setItem('id', id);
                                sessionStorage.setItem('hideShow', true);
                                setLoading(false);
                                navigate(`/Claim/ContractClaimApproval/ContractConsolidate/${id}`);
                            }
                            else if (isGmailAndId) {
                                // Distributor Claim Login
                                sessionStorage.setItem('email', paramEmail);
                                sessionStorage.setItem('id', id);
                                setLoading(false);
                                navigate(PATH_MASTERS.DistributorClaimApproval);
                            } else {
                                setLoading(false);
                                // Regular Login
                                navigate(PATH_MASTERS.root);
                            }
                            setErrorMessage(false)
                        } else {
                            setValidationMessage('Invalid username or password.');
                            setLoading(false);
                        }
                    }).catch((err) => {
                        if (err?.response?.status === 409) {
                            setIsDataImporting(true);
                            checkDataImportStatus();
                            setValidationMessage('System under maintenance. Data importing is currently in progress.');
                        } else {
                            setValidationMessage('Something went wrong. Please try again.');
                        }
                        setValidationMessage(err?.response?.data?.message || 'Something went wrong. Please try again.');
                        setLoading(false);
                    });

            });
        } catch (error) {
            setValidationMessage('An error occurred. Please try again.');
            setTimeout(() => {
                setLoading(false);
            }, 500); // Delay of 1.5 seconds
        }
    };
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // NOSONAR

    const getImportingBannerDetails = () => {
        const rawTotalTime = Number(uptimeData?.totalImportingTime || 0);
        const startTime = uptimeData?.latestStartTime ? new Date(uptimeData.latestStartTime) : null;

        let estimatedTime = targetCompletionTime || (uptimeData?.estimatedCompletionTime ? new Date(uptimeData.estimatedCompletionTime) : null);
        let totalMinutes = rawTotalTime;

        if (totalMinutes === 0) {
            if (estimatedTime && startTime) {
                totalMinutes = Math.max(1, Math.round((estimatedTime.getTime() - startTime.getTime()) / 60000));
            } else {
                totalMinutes = 3;
            }
        }

        if (!estimatedTime) {
            if (startTime) {
                estimatedTime = new Date(startTime.getTime() + totalMinutes * 60000);
            } else if (liveServerTime) {
                estimatedTime = new Date(liveServerTime.getTime() + totalMinutes * 60000);
            }
        }

        const now = liveServerTime || new Date();
        let remainingMinutes = 0;
        if (estimatedTime && now) {
            const diffMs = estimatedTime.getTime() - now.getTime();
            remainingMinutes = Math.ceil(diffMs / 60000);
        }

        return {
            totalMinutes,
            startTime,
            estimatedTime,
            remainingMinutes
        };
    };

    return (
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={4}>
                <img src="/logo/ProClaimz Logo.jpg" alt=" " style={{ height: 70, objectFit: 'contain' }} />

                {/* ── Data Import Warning Banner ────────────────────────────── */}
                {isDataImporting && (() => {
                    const details = getImportingBannerDetails();
                    const isElapsed = details.remainingMinutes <= 0;

                    return (
                        <Alert
                            severity="warning"
                            sx={{
                                fontSize: '13px',
                                fontWeight: 600,
                                '& .MuiAlert-icon': { fontSize: '22px' },
                            }}
                        >
                            {isElapsed ? (
                                <div>
                                    <div>Data import is still in progress.</div>
                                    <div style={{ marginTop: '8px' }}>
                                        It is taking longer than expected. Please wait a little longer.
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <div>
                                        Data import is currently in progress. It will take approximately {details.remainingMinutes} {details.remainingMinutes === 1 ? 'minute' : 'minutes'} to complete.
                                    </div>                      
                                    {details.estimatedTime ? (
                                        <div style={{ marginTop: '8px' }}>
                                            Please try again after {formatTimeOnly(details.estimatedTime)}.
                                        </div>
                                    ) : (
                                        <div style={{ marginTop: '8px' }}>Data import is currently in progress. Please try again later.</div>
                                    )}
                                </div>
                            )}
                        </Alert>
                    );
                })()}

                <div>
                    <TextField
                        {...register('email')}
                        label="User Name"
                        size="small"
                        disabled={isDataImporting}
                        InputProps={{
                            readOnly: Boolean(paramEmail), // Read-only if dynamic email is present
                        }}
                        fullWidth
                        sx={{
                            transition: 'opacity 0.5s ease', // Smooth fade effect
                            opacity: paramEmail || isDataImporting ? 0.5 : 1, // Fade effect when pre-filled or importing
                            pointerEvents: paramEmail || isDataImporting ? 'none' : 'auto', // Disable interaction if pre-filled or importing
                            '& .MuiInputBase-input': {
                                fontSize: '13px',  // Set text size to 13px
                            },
                            '& .MuiFormLabel-root': {
                                fontSize: '14px',  // Set label text size to 14px
                            },
                        }}
                    />

                    {errorMessage && (!values.email || !emailRegex.test(values.email)) && (
                        <FormHelperText style={{ color: 'red', fontSize: '12px' }} >
                            {!values.email ? "Email is required" : ""} {/* This message is now removed -  Email must be a valid email address */}
                        </FormHelperText>
                    )}
                </div>
                <div>
                    <TextField
                        {...register('password')}
                        label="Password"
                        size="small"
                        disabled={isDataImporting}
                        type={showPassword ? 'text' : 'password'}
                        fullWidth
                        sx={{
                            transition: 'opacity 0.5s ease', // Smooth fade effect
                            opacity: isDataImporting ? 0.5 : 1,
                            '& .MuiInputBase-input': {
                                fontSize: '13px',  // Set text size to 13px
                            },
                            '& .MuiFormLabel-root': {
                                fontSize: '14px',  // Set label text size to 14px
                            },
                        }}

                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" disabled={isDataImporting}>
                                        <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}

                    />
                    {errorMessage && !values.password && (
                        <FormHelperText style={{ color: 'red', fontSize: '12px' }} >
                            Password is required
                        </FormHelperText>
                    )}
                </div>
                {validationMessage && (
                    <Typography color="red" variant="body2" sx={{ textAlign: 'center' }}>
                        {validationMessage}
                    </Typography>
                )}
            </Stack>

            <Stack alignItems="flex-end" sx={{ my: 2 }} />

            <LoadingButton
                fullWidth
                color="inherit"
                size="large"
                type="submit"
                variant="contained"
                disabled={isDataImporting}
                sx={{
                    bgcolor: isDataImporting ? '#e0e0e0' : '#CCF4FE',
                    fontSize: '13px',
                    '&:hover': {
                        bgcolor: isDataImporting ? '#e0e0e0' : '#99E3FF',
                    },
                }}
            >
                Login
            </LoadingButton>
            {loading && <LoadingProgressBar />}



        </FormProvider>
    );
}
